/**
 * What react-router shows when a route throws or does not exist.
 *
 * Without an errorElement, react-router renders its own developer-facing
 * screen — the raw message and a stack — to whoever happens to be looking.
 * A mistyped URL is not an incident, and a real fault should not spill its
 * internals, so this says what happened in plain words and offers a way out.
 * The detail still goes to the console for whoever is debugging.
 */
import type { ReactElement } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';

export function RouteErrorPage({ missing = false }: { missing?: boolean }): ReactElement {
  const error = useRouteError();
  const notFound = missing || (isRouteErrorResponse(error) && error.status === 404);
  if (!notFound) console.error('[route error]', error);

  return (
    <main data-testid="route-error" style={{ maxWidth: 560, margin: '64px auto', padding: 24 }}>
      <h1 style={{ fontSize: 22, marginTop: 0 }}>
        {notFound ? 'Page not found' : 'Something went wrong'}
      </h1>
      <p style={{ color: 'var(--text-muted)' }}>
        {notFound
          ? 'That address does not match anything in AutoeCTD. It may have been mistyped or moved.'
          : 'The page could not be displayed. Try again, and tell an administrator if it keeps happening.'}
      </p>
      <a href="/home" data-testid="route-error-home">Go to AutoeCTD</a>
    </main>
  );
}
